// Rotas do painel da equipe ("/painel", DESIGN §7). Só entram no build web: no app nativo (ADR app nativo, D6)
// rotas.tsx não chama `criarRotasPainel`, e os chunks do painel nem são gerados.
import type { Permissao } from "@grindset/server/contratos";
import type { RouteObject } from "react-router";
import { EmConstrucao } from "./compartilhado/ui";
import { SomenteVisitante } from "./guardas";
import { ExigirEquipe, ExigirPermissao } from "./guardas.painel";
import { tela } from "./rotas.comum";
import { LEGADO_PAINEL, rotasLegado } from "./rotas.legado";

/** Rota do painel que exige permissões (a API confere de novo). */
function com(permissoes: readonly Permissao[], rota: RouteObject): RouteObject {
  return { element: <ExigirPermissao permissoes={permissoes} />, children: [rota] };
}

/** Rotas de /painel. Função (e não constante) para o build nativo descartar o módulo inteiro (rotas.tsx). */
export function criarRotasPainel(): RouteObject[] {
  return [
    // Caminhos antigos de links de avisos: só redirecionam (o destino aplica o guarda)
    ...rotasLegado(LEGADO_PAINEL),
    {
      path: "/painel/entrar",
      element: <SomenteVisitante area="equipe" />,
      children: [{ index: true, ...tela(() => import("./painel/acesso/Entrar")) }],
    },
    {
      path: "/painel/trocar-senha",
      element: <ExigirEquipe permitirTrocaSenha />,
      children: [{ index: true, ...tela(() => import("./painel/acesso/TrocarSenha")) }],
    },
    {
      path: "/painel",
      element: <ExigirEquipe />,
      children: [
        // Tela cheia da recepção (quiosque), fora do layout
        com(["recepcao.qr"], {
          path: "checkin/recepcao",
          ...tela(() => import("./painel/recepcao/Recepcao")),
        }),
        {
          ...tela(() => import("./painel/LayoutPainel")),
          children: [
            // Cada tela exige as permissões do mesmo destino da navegação (docs/seguranca/RBAC.md).
            {
              element: <ExigirPermissao permissoes={["visao-geral.ver"]} inicio />,
              children: [{ index: true, ...tela(() => import("./painel/visao-geral/VisaoGeral")) }],
            },
            com(["alunos.buscar"], { path: "alunos", ...tela(() => import("./painel/alunos/Alunos")) }),
            com(["alunos.cadastrar"], {
              path: "alunos/novo",
              ...tela(() => import("./painel/alunos/NovoAluno")),
            }),
            com(["alunos.ver"], { path: "alunos/:id", ...tela(() => import("./painel/alunos/FichaAluno")) }),
            {
              // Dividir (transferir) ou Pedidos/Histórico (aprovar): basta uma; cada aba confere a sua.
              element: <ExigirPermissao permissoes={[]} qualquer={["alunos.transferir", "alunos.aprovar-transferencia"]} />,
              children: [{ path: "carteira", ...tela(() => import("./painel/carteira/Carteira")) }],
            },
            com(["checkin.registrar"], {
              path: "checkin",
              ...tela(() => import("./painel/recepcao/Checkin")),
            }),
            com(["treino.prescrever"], { path: "treinos", ...tela(() => import("./painel/treino/Treinos")) }),
            com(["treino.prescrever"], {
              path: "treinos/fichas/:id",
              ...tela(() => import("./painel/treino/EditorFicha")),
            }),
            com(["mensagens.usar"], {
              path: "mensagens",
              ...tela(() => import("./painel/mensagens/Mensagens")),
            }),
            com(["mensagens.usar"], {
              path: "mensagens/:alunoId",
              ...tela(() => import("./painel/mensagens/Mensagens")),
            }),
            com(["desafios.ver"], { path: "desafios", ...tela(() => import("./painel/desafios/Desafios")) }),
            com(["desafios.gerir"], {
              path: "desafios/novo",
              ...tela(() => import("./painel/desafios/NovoDesafio")),
            }),
            com(["desafios.ver"], {
              path: "desafios/:id",
              ...tela(() => import("./painel/desafios/DesafioDetalhe")),
            }),
            com(["premios.entregar"], { path: "premios", ...tela(() => import("./painel/premios/Premios")) }),
            { path: "avisos", ...tela(() => import("./painel/avisos/Avisos")) },
            com(["comunidade.publicar"], {
              path: "timeline",
              ...tela(() => import("./painel/comunidade/Timeline")),
            }),
            com(["eventos.operar"], { path: "eventos", ...tela(() => import("./painel/eventos/Eventos")) }),
            com(["eventos.operar"], {
              path: "eventos/:id",
              ...tela(() => import("./painel/eventos/EventoDetalhe")),
            }),
            com(["avaliacoes.registrar"], {
              path: "avaliacoes",
              ...tela(() => import("./painel/avaliacoes/Avaliacoes")),
            }),
            com(["pontos-extras.avaliar"], {
              path: "pontos-extras",
              ...tela(() => import("./painel/pontos/PontosExtras")),
            }),
            com(["sorteios.gerir"], {
              path: "sorteios/:id",
              ...tela(() => import("./painel/desafios/SorteioDetalhe")),
            }),
            com(["feedback.ver", "feedback.responder"], {
              path: "feedback",
              ...tela(() => import("./painel/feedback/Feedback")),
            }),
            com(["indicacoes.registrar", "indicacoes.gerir"], {
              path: "indicacoes",
              ...tela(() => import("./painel/indicacoes/Indicacoes")),
            }),
            com(["suspeitas.ver"], {
              path: "suspeitas",
              ...tela(() => import("./painel/suspeitas/Suspeitas")),
            }),
            com(["automacoes.ver", "automacoes.gerir"], {
              path: "automacoes",
              ...tela(() => import("./painel/configuracoes/Automacoes")),
            }),
            // Abrem em modo leitura com `gamificacao.ver`; editar exige `regras.gerir` (botões somem sem ela).
            com(["gamificacao.ver"], {
              path: "config/pontos",
              ...tela(() => import("./painel/configuracoes/RegrasPontos")),
            }),
            // Conquistas cadastráveis (docs/prototipo/conquistas-cadastro.html §5, §6): criar e editar com `regras.gerir`.
            com(["gamificacao.ver", "regras.gerir"], {
              path: "config/gamificacao/conquistas/nova",
              ...tela(() => import("./painel/configuracoes/conquistas/NovaConquista")),
            }),
            com(["gamificacao.ver", "regras.gerir"], {
              path: "config/gamificacao/conquistas/nova/:criterio",
              ...tela(() => import("./painel/configuracoes/conquistas/EditorConquista")),
            }),
            com(["gamificacao.ver", "regras.gerir"], {
              path: "config/gamificacao/conquistas/:id",
              ...tela(() => import("./painel/configuracoes/conquistas/EditorConquista")),
            }),
            com(["gamificacao.ver"], {
              path: "config/gamificacao",
              ...tela(() => import("./painel/configuracoes/Gamificacao")),
            }),
            com(["configuracoes.gerir"], {
              path: "config/unidade",
              ...tela(() => import("./painel/configuracoes/Unidade")),
            }),
            com(["equipe.gerir"], {
              path: "config/equipe",
              ...tela(() => import("./painel/configuracoes/Equipe")),
            }),
            com(["permissoes.gerir"], {
              path: "config/permissoes",
              ...tela(() => import("./painel/configuracoes/Permissoes")),
            }),
            com(["integracao.gerir"], {
              path: "config/integracao",
              ...tela(() => import("./painel/configuracoes/Integracao")),
            }),
            com(["auditoria.ver"], {
              path: "config/auditoria",
              ...tela(() => import("./painel/configuracoes/Auditoria")),
            }),
            com(["resultados.ver", "resultados.exportar"], {
              path: "resultados",
              ...tela(() => import("./painel/resultados/Resultados")),
            }),
            { path: "*", element: <EmConstrucao titulo="Página não encontrada" /> },
          ],
        },
      ],
    },
  ];
}
